<template>
  <div class="app-tabs">
    <TabGroup as="div" v-bind="$attrs" :selected-index="selectedIndex" @change="onChange">
      <TabList>
      <!-- Use for mobile can, use an "onChange" listener to redirect the user to the selected tab URL. -->
      <div class="sm:hidden">
        <label for="tabs" class="sr-only">Select a tab</label>
        <select id="tabs" label="tabs" class="block w-full rounded-md border-slate-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm" @change="handleMobileTabChange">
          <option v-for="(item, index) in items" :key="item.label" :value="index" :selected="index === selectedIndex">{{ item.label }}</option>
        </select>
      </div>
      <div class="hidden sm:block">
        <div class="border-b border-slate-300">
          <nav class="-mb-px flex space-x-8" aria-label="Tabs">
            <Tab 
              v-for="(item, index) in items"
              :key="item.label"
              v-slot="{ selected, disabled }"
              :disabled="item.disabled || false"
              as="template"
            >

              <RouterLink
                v-if="item.to"
                as="button" 
                :to="item.to"
                :class="[
                  selected ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-black',
                  'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium focus:outline-none'
                ]"
              >
                <span class="truncate">{{ item.label }}</span>
              </RouterLink>
              <button
                v-else
                :class="[
                  selected ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-black',
                  'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium focus:outline-none'
                ]"
              >
                <slot name="icon" :item="item" :index="index" :selected="selected" :disabled="disabled"></slot>
                <slot :item="item" :index="index" :selected="selected" :disabled="disabled">
                  <span class="truncate">{{ item.label }}</span>
                </slot>
                <slot :name="`${item.slot}-badge` || 'badge'" :item="item" :index="index" :selected="selected" :disabled="disabled">
                  <span 
                    v-if="item.badge"
                    :class="[
                      selected ? 'bg-secondary text-primary' : 'bg-slate-100 text-balack',
                      'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block'
                    ]"
                  >
                    {{ item.badge }}
                  </span>
                </slot>
              </button>
            </Tab>
          </nav>
        </div>
      </div>
      </TabList>

      <!-- Tab content -->
      <div class="tab-content py-4">
        <TabPanels v-if="content" >
          <TabPanel v-for="(item, index) of items" :key="index" v-slot="{ selected }">
            <slot :name="item.slot || 'content'" :item="item" :index="index" :selected="selected">
              {{ item.content }}
            </slot>
          </TabPanel>
        </TabPanels>
      </div>
    </TabGroup>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref, watch } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'

export type TabItem = {
  label: string
  content?: string
  badge?: string | number | undefined
  disabled?: boolean,
  slot?: string
  to?: RouteLocationRaw
}

const props = defineProps({
  items: {
    type: Array as PropType<TabItem[]>,
    required: true,
  },
  content: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Number,
    default: undefined
  },
  defaultIndex: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update:modelValue', 'change'])
const selectedIndex = ref<number | undefined>(props.modelValue || props.defaultIndex)
const router = useRouter()

watch(() => props.modelValue, (value) => selectedIndex.value = value )
    
const onChange = (index: number) =>{
  selectedIndex.value = index

  emit('change', index)

  if (props.modelValue !== undefined) {
    emit('update:modelValue', selectedIndex.value)
  }
}

const handleMobileTabChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const newIndex = parseInt(target.value)
  selectedIndex.value = newIndex
  emit('change', newIndex)

  if (props.modelValue !== undefined) {
    emit('update:modelValue', newIndex)
  }
  const selectedItem = props.items[newIndex]
  if (selectedItem && selectedItem.to) {
    router.push(selectedItem.to)
  }
}
</script>